import type { AccountDetailTypeValue, AccountTypeValue, CashFlowSectionValue } from '../accounting/chart';
import { roundHalfUpCents } from '../money';

/**
 * The importer's mappings as rows (piece 31; decision 1055): the account-type
 * map, the item field map and the matching rules a run reads once, when it
 * starts. 🔒 The seed writes each table with the values below and nothing else
 * carries a copy: a run reads the rows, so a person's edit at Settings ›
 * Integrations › Import Mappings reaches the next run.
 */

// ---- The account-type map ----

export interface ImportAccountTypeSeedRow {
  sourceAccountType: string;
  accountType: AccountTypeValue;
  detailType: AccountDetailTypeValue;
  cashFlowSection: CashFlowSectionValue;
  bandFrom: number;
  bandTo: number;
}

/**
 * 🔒 A Zoho account type in the words a person reads (decision 6684): its
 * underscores become spaces and its first letter is capital, so `input_tax`
 * reads *Input tax*. The page draws the type's own name small beneath it.
 */
export function zohoAccountTypeWords(sourceAccountType: string): string {
  const spaced = sourceAccountType.replace(/_/g, ' ').trim();
  return spaced.length === 0 ? spaced : `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`;
}

function types(sourceTypes: readonly string[], accountType: AccountTypeValue, detailType: AccountDetailTypeValue, cashFlowSection: CashFlowSectionValue, bandFrom: number, bandTo: number): ImportAccountTypeSeedRow[] {
  return sourceTypes.map((sourceAccountType) => ({ sourceAccountType, accountType, detailType, cashFlowSection, bandFrom, bandTo }));
}

/** 🔒 The table under piece 31's block 1, one row per Zoho `account_type` (decisions 0221, 1055); the suite holds it to the table. */
export const ZOHO_ACCOUNT_TYPE_SEED: readonly ImportAccountTypeSeedRow[] = [
  ...types(['cash', 'bank'], 'Asset', 'CurrentAsset', 'Excluded', 1000, 1499),
  ...types(['accounts_receivable', 'other_current_asset', 'stock', 'payment_clearing', 'input_tax'], 'Asset', 'CurrentAsset', 'Operating', 1000, 1499),
  ...types(['fixed_asset'], 'Asset', 'FixedAsset', 'Investing', 1500, 1799),
  ...types(['intangible_asset', 'non_current_asset', 'other_asset'], 'Asset', 'OtherAsset', 'Investing', 1800, 1999),
  ...types(['deferred_tax_asset'], 'Asset', 'OtherAsset', 'Operating', 1800, 1999),
  ...types(['accounts_payable', 'credit_card', 'other_current_liability', 'output_tax', 'tax_payable', 'overseas_tax_payable'], 'Liability', 'CurrentLiability', 'Operating', 2000, 2499),
  ...types(['long_term_liability', 'non_current_liability', 'other_liability'], 'Liability', 'LongTermLiability', 'Financing', 2500, 2999),
  ...types(['deferred_tax_liability'], 'Liability', 'LongTermLiability', 'Operating', 2500, 2999),
  ...types(['equity'], 'Equity', 'EquityDetail', 'Financing', 3000, 3999),
  ...types(['income'], 'Revenue', 'Income', 'Operating', 4000, 4999),
  ...types(['other_income'], 'Revenue', 'OtherIncome', 'Operating', 4000, 4999),
  ...types(['cost_of_goods_sold'], 'Expense', 'CostOfGoodsSold', 'Operating', 5000, 5999),
  ...types(['expense'], 'Expense', 'OperatingExpense', 'Operating', 6000, 6999),
  ...types(['other_expense'], 'Expense', 'OtherExpense', 'Operating', 7000, 7999),
];

/** A source's account type as the rows key it: trimmed and lower-cased. */
export function accountTypeKey(sourceType: string): string {
  return sourceType.trim().toLowerCase();
}

/** 🔒 Whether an edited account-type row may stand, as the refusal a person reads, or null. */
export function accountTypeRowProblem(row: { bandFrom: number; bandTo: number }, detailTypeFits: boolean): string | null {
  if (!detailTypeFits) return 'That subtype is not one its type admits; choose a subtype of the type.';
  if (!Number.isInteger(row.bandFrom) || !Number.isInteger(row.bandTo) || row.bandFrom < 1000 || row.bandTo > 9999) return 'A band runs between two four-digit account numbers.';
  if (row.bandFrom > row.bandTo) return 'The band starts after it ends; put the lower number first.';
  return null;
}

// ---- The item field map ----

/** The seam's item fields a row may read, never Zoho's own field names. */
export const ITEM_SEAM_FIELDS = [
  'name', 'sku', 'partNumber', 'manufacturer', 'description', 'purchaseDescription', 'rateCents', 'purchaseRateCents', 'unit', 'isService', 'isActive', 'salesAccountId', 'purchaseAccountId',
] as const;
export type ItemSeamField = (typeof ITEM_SEAM_FIELDS)[number];

/** The Item fields the catalog form writes, which the editor offers and nothing else. */
export const ITEM_TARGET_FIELDS = [
  'name', 'sku', 'manufacturerPartNumber', 'manufacturerCompanyId', 'salesDescription', 'purchaseDescription', 'salesPrice', 'purchasePrice', 'unitId', 'salesAccountId', 'purchaseAccountId', 'status', 'type',
  // 🔒 Decision 1921: the list price a buy and a sell spread from (piece 16), written as given and never re-derived here.
  'listPrice',
] as const;
export type ItemTargetField = (typeof ITEM_TARGET_FIELDS)[number];

/** The conversions the code implements; a row picks one, and no row writes a new one. */
export const IMPORT_CONVERSIONS = ['Copy', 'CentsToDollars', 'UnitRule', 'ManufacturerByName', 'AccountBySourceRef', 'ActiveToStatus', 'ServiceToType'] as const;
export type ImportConversionValue = (typeof IMPORT_CONVERSIONS)[number];

/** Which seam fields each conversion reads and which Item fields it writes. */
export const CONVERSION_FITS: Readonly<Record<ImportConversionValue, { sources: readonly ItemSeamField[]; targets: readonly ItemTargetField[] }>> = {
  Copy: { sources: ['name', 'sku', 'partNumber', 'description', 'purchaseDescription'], targets: ['name', 'sku', 'manufacturerPartNumber', 'salesDescription', 'purchaseDescription'] },
  CentsToDollars: { sources: ['rateCents', 'purchaseRateCents'], targets: ['salesPrice', 'purchasePrice', 'listPrice'] },
  UnitRule: { sources: ['unit'], targets: ['unitId'] },
  ManufacturerByName: { sources: ['manufacturer'], targets: ['manufacturerCompanyId'] },
  AccountBySourceRef: { sources: ['salesAccountId', 'purchaseAccountId'], targets: ['salesAccountId', 'purchaseAccountId'] },
  ActiveToStatus: { sources: ['isActive'], targets: ['status'] },
  ServiceToType: { sources: ['isService'], targets: ['type'] },
};

/** 🔒 Whether a field row may stand (decision 1055), as the refusal a person reads, or null. */
export function fieldMappingProblem(row: { sourceField: string; targetField: string; conversion: string }): string | null {
  if (!(ITEM_SEAM_FIELDS as readonly string[]).includes(row.sourceField)) return 'That field is not one the Books source carries for an item.';
  if (!(ITEM_TARGET_FIELDS as readonly string[]).includes(row.targetField)) return 'That is not a field the catalog form writes; choose one from the list.';
  if (!(IMPORT_CONVERSIONS as readonly string[]).includes(row.conversion)) return 'That conversion is not one the importer carries out; choose one from the list.';
  const fit = CONVERSION_FITS[row.conversion as ImportConversionValue];
  if (!(fit.sources as readonly string[]).includes(row.sourceField) || !(fit.targets as readonly string[]).includes(row.targetField)) {
    return 'That conversion does not carry this source field into that item field; choose a conversion that fits both.';
  }
  return null;
}

export interface ImportFieldSeedRow {
  sourceField: ItemSeamField;
  targetField: ItemTargetField;
  conversion: ImportConversionValue;
}

/** 🔒 Block 3's conversions, one row per Item field a pull writes (decision 1055). */
export const ITEM_FIELD_SEED: readonly ImportFieldSeedRow[] = [
  { sourceField: 'name', targetField: 'name', conversion: 'Copy' },
  { sourceField: 'sku', targetField: 'sku', conversion: 'Copy' },
  { sourceField: 'partNumber', targetField: 'manufacturerPartNumber', conversion: 'Copy' },
  { sourceField: 'manufacturer', targetField: 'manufacturerCompanyId', conversion: 'ManufacturerByName' },
  { sourceField: 'description', targetField: 'salesDescription', conversion: 'Copy' },
  { sourceField: 'purchaseDescription', targetField: 'purchaseDescription', conversion: 'Copy' },
  { sourceField: 'rateCents', targetField: 'salesPrice', conversion: 'CentsToDollars' },
  { sourceField: 'purchaseRateCents', targetField: 'purchasePrice', conversion: 'CentsToDollars' },
  { sourceField: 'unit', targetField: 'unitId', conversion: 'UnitRule' },
  { sourceField: 'salesAccountId', targetField: 'salesAccountId', conversion: 'AccountBySourceRef' },
  { sourceField: 'purchaseAccountId', targetField: 'purchaseAccountId', conversion: 'AccountBySourceRef' },
  { sourceField: 'isActive', targetField: 'status', conversion: 'ActiveToStatus' },
  { sourceField: 'isService', targetField: 'type', conversion: 'ServiceToType' },
];

// ---- The matching rules ----

export const IMPORT_RULE_SOURCE_KINDS = ['Account', 'Tax', 'Contact', 'ContactPerson', 'Item', 'Estimate', 'SalesOrder', 'Invoice', 'CreditNote', 'CustomerPayment', 'PurchaseOrder', 'Bill', 'VendorCredit', 'VendorPayment', 'Expense', 'ManualJournal'] as const;
export type ImportRuleSourceKindValue = (typeof IMPORT_RULE_SOURCE_KINDS)[number];

/** 🔨 The natural keys piece 31 states and nothing fuzzier (decision 0238): a new key kind is a code change with its own decision. */
export const IMPORT_KEY_KINDS = [
  'AccountNumber', 'AccountName', 'TaxNameAndRate', 'Ein', 'EmailDomain', 'NameAndPostalCode', 'WorkEmail', 'NameAtCompany', 'ManufacturerAndPartNumber', 'Sku', 'DocumentNumber', 'VendorAndNumber', 'PaymentParticulars', 'ReferenceAndDate',
  // 🔒 Decision 8381: a CRM manufacturer's folded name against the makers items here name; a Zoho CRM rule alone.
  'ItemMaker',
  // 🔒 Decision 12360: a conversion rule beside the matching ones, off until the owner ticks it; it matches no record.
  'CashModeCheck',
] as const;
export type ImportKeyKindValue = (typeof IMPORT_KEY_KINDS)[number];

export const IMPORT_RULE_GRADES = ['Exact', 'Weak'] as const;
export type ImportRuleGradeValue = (typeof IMPORT_RULE_GRADES)[number];

export interface ImportMatchRuleSeedRow {
  sourceKind: ImportRuleSourceKindValue;
  keyKind: ImportKeyKindValue;
  rank: number;
  grade: ImportRuleGradeValue;
  /** Seeded off where the owner ticks it on (decision 12360); left out, a rule seeds on. */
  isActive?: boolean;
}

/**
 * 🔒 The natural keys piece 31 states, ranked in the order the matcher tries
 * them today (decision 1055). Exact: the EIN, a contact's website domain (decisions 6332, 7880), the work email, manufacturer and
 * part number, the account number, the account name on its own side (decision 7840), the tax's name and rate, a document's number
 * (an estimate's, a sales order's and a purchase order's since decision 3340)
 * and a bill's vendor and number. Weak: name and postal code, name at the
 * company, the SKU alone, a payment's particulars, and
 * a journal's reference and date.
 */
export const IMPORT_MATCH_RULE_SEED: readonly ImportMatchRuleSeedRow[] = [
  { sourceKind: 'Account', keyKind: 'AccountNumber', rank: 1, grade: 'Exact' },
  // 🔒 Decision 7840: an identical folded name on the account's own side is the same account.
  { sourceKind: 'Account', keyKind: 'AccountName', rank: 2, grade: 'Exact' },
  { sourceKind: 'Tax', keyKind: 'TaxNameAndRate', rank: 1, grade: 'Exact' },
  { sourceKind: 'Contact', keyKind: 'Ein', rank: 1, grade: 'Exact' },
  // 🔒 Decision 6332: the domain of the contact's website or its people's emails, between the tax number and the name.
  { sourceKind: 'Contact', keyKind: 'EmailDomain', rank: 2, grade: 'Exact' },
  { sourceKind: 'Contact', keyKind: 'NameAndPostalCode', rank: 3, grade: 'Weak' },
  { sourceKind: 'ContactPerson', keyKind: 'WorkEmail', rank: 1, grade: 'Exact' },
  { sourceKind: 'ContactPerson', keyKind: 'NameAtCompany', rank: 2, grade: 'Weak' },
  { sourceKind: 'Item', keyKind: 'ManufacturerAndPartNumber', rank: 1, grade: 'Exact' },
  { sourceKind: 'Item', keyKind: 'Sku', rank: 2, grade: 'Weak' },
  { sourceKind: 'Estimate', keyKind: 'DocumentNumber', rank: 1, grade: 'Exact' },
  { sourceKind: 'SalesOrder', keyKind: 'DocumentNumber', rank: 1, grade: 'Exact' },
  { sourceKind: 'Invoice', keyKind: 'DocumentNumber', rank: 1, grade: 'Exact' },
  { sourceKind: 'CreditNote', keyKind: 'DocumentNumber', rank: 1, grade: 'Exact' },
  { sourceKind: 'CustomerPayment', keyKind: 'PaymentParticulars', rank: 1, grade: 'Weak' },
  { sourceKind: 'PurchaseOrder', keyKind: 'DocumentNumber', rank: 1, grade: 'Exact' },
  { sourceKind: 'Bill', keyKind: 'VendorAndNumber', rank: 1, grade: 'Exact' },
  { sourceKind: 'VendorCredit', keyKind: 'DocumentNumber', rank: 1, grade: 'Exact' },
  { sourceKind: 'VendorPayment', keyKind: 'PaymentParticulars', rank: 1, grade: 'Weak' },
  // 🔒 Decision 12360: Zoho's default mode was Cash and the checks were keyed under it; off until the owner ticks it.
  { sourceKind: 'VendorPayment', keyKind: 'CashModeCheck', rank: 2, grade: 'Exact', isActive: false },
  { sourceKind: 'Expense', keyKind: 'ReferenceAndDate', rank: 1, grade: 'Weak' },
  { sourceKind: 'ManualJournal', keyKind: 'ReferenceAndDate', rank: 1, grade: 'Weak' },
];

/**
 * 🔒 The Zoho CRM preset's own rule (decision 8381): the maker an item here names, tried first for a CRM manufacturer.
 * It is seeded under the `ZohoCrm` source system, and a CRM run reads it before block 2's rules, which it reads as they stand.
 */
export const CRM_MATCH_RULE_SEED: readonly ImportMatchRuleSeedRow[] = [
  { sourceKind: 'Contact', keyKind: 'ItemMaker', rank: 1, grade: 'Exact' },
];

// ---- What Settings › Integrations › Import Mappings reads and sends ----

export interface ImportAccountTypeMappingView extends ImportAccountTypeSeedRow {
  id: string;
  isActive: boolean;
  updatedBy: string | null;
  updatedAt: string;
}

export interface ImportFieldMappingView {
  id: string;
  sourceKind: 'Item';
  sourceField: string;
  targetField: string;
  conversion: ImportConversionValue;
  isActive: boolean;
  updatedBy: string | null;
  updatedAt: string;
}

export interface ImportMatchRuleView {
  id: string;
  sourceKind: ImportRuleSourceKindValue;
  keyKind: ImportKeyKindValue;
  rank: number;
  grade: ImportRuleGradeValue;
  isActive: boolean;
  updatedBy: string | null;
  updatedAt: string;
}

// ---- The item details (decision 1676) ----

/** 🔒 Where a Zoho item detail lands; a detail no row answers is asked about once. ItemField writes one Item column (decision 1921). */
export const ITEM_DETAIL_TARGETS = ['Ignore', 'Category', 'Attribute', 'TaxCategory', 'Supplier', 'ItemField'] as const;
export type ItemDetailTargetValue = (typeof ITEM_DETAIL_TARGETS)[number];

/**
 * 🔒 The targets one detail is offered (decision 1921, amending 1676). Every
 * detail is offered the preferred vendor, whose value is a company name, since
 * a custom field can carry one as well as Zoho's own Preferred vendor.
 */
export function itemDetailTargetsFor(_label: string): readonly ItemDetailTargetValue[] {
  return ITEM_DETAIL_TARGETS;
}

/** 🔒 The Item columns an ItemField detail writes (decision 1921): the list price alone today. */
export const ITEM_DETAIL_ITEM_FIELDS = ['listPrice'] as const;
export type ItemDetailItemField = (typeof ITEM_DETAIL_ITEM_FIELDS)[number];

/**
 * 🔒 The item fields the first question's picker offers (decision 1921): the
 * list price, the tax category and the preferred vendor, each the target it
 * writes through. A field no listed choice names is reached by creating the
 * attribute, as before.
 */
export const ITEM_DETAIL_FIELD_CHOICES = [
  { key: 'listPrice', target: 'ItemField', itemField: 'listPrice' },
  { key: 'taxCategory', target: 'TaxCategory', itemField: null },
  { key: 'preferredVendor', target: 'Supplier', itemField: null },
] as const satisfies readonly { key: string; target: ItemDetailTargetValue; itemField: ItemDetailItemField | null }[];
export type ItemDetailFieldChoice = (typeof ITEM_DETAIL_FIELD_CHOICES)[number]['key'];

/**
 * 🔒 A detail's text as a money amount in dollars (decision 1921): thousands
 * separators and a dollar sign fold away, and the cents round half up as the
 * adapter rounds a rate. Text that is no amount answers null.
 */
export function detailDollars(value: string): number | null {
  const text = value.trim().replace(/[$,\s]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
  return roundHalfUpCents(Number(text) * 100) / 100;
}

/** One value a detail was seen with, how many items carried it, and the rule it maps through where one is held. */
export interface ItemDetailValueView {
  sourceValue: string;
  itemCount: number;
  ruleId: string | null;
  categoryId: string | null;
  taxCategoryId: string | null;
}

/** One detail the last items read found, or one a person answered that the read did not meet. */
export interface ItemDetailView {
  label: string;
  itemCount: number;
  /** Every value seen, the most carried first. */
  values: ItemDetailValueView[];
  /** The person's answer, or none while the detail waits on its question. */
  mapping: { id: string; target: ItemDetailTargetValue; attributeTypeId: string | null; attributeName: string | null; itemField?: ItemDetailItemField | null; updatedBy: string | null; updatedAt: string } | null;
  targets: readonly ItemDetailTargetValue[];
}

/** A Category rule, *when detail X is Y, category Z*, in rank order. */
export interface ItemDetailRuleView {
  id: string;
  detailLabel: string;
  sourceValue: string;
  categoryName: string;
  rank: number;
}

export interface ItemDetailsView {
  /** The items read the details were read from, or none before the first. */
  lastRead: { runId: string; startedAt: string } | null;
  details: ItemDetailView[];
  categoryRules: ItemDetailRuleView[];
  options: {
    categories: { id: string; label: string }[];
    taxCategories: { id: string; label: string }[];
    /** The active Enumerated catalog attributes an Attribute row can name. */
    attributeTypes: { id: string; label: string }[];
    /** 🔒 The item fields the first question offers (decision 1921): the list price only where no active field row writes it. */
    itemFields?: ItemDetailFieldChoice[];
  };
}

export interface ImportMappingsView {
  accountTypes: ImportAccountTypeMappingView[];
  fields: ImportFieldMappingView[];
  /** In source-kind order, then by rank within a kind. */
  rules: ImportMatchRuleView[];
  /** 🔒 The item details (decision 1676); absent from an answer that predates them. */
  itemDetails?: ItemDetailsView;
  /** 🔒 The attachment answers (decision 3480), one row per Books document type with a read; absent from an answer that predates them. */
  attachments?: ImportAttachmentClassView[];
}

/**
 * 🔒 One Books document type's attachment answer (piece 31; decision 3480):
 * the type its files import under and whether they are confidential, beside the
 * types its record offers. An unanswered type names no file type and reads
 * Confidential, which is where its files land until it is answered.
 */
export interface ImportAttachmentClassView {
  /** The Books document type, named as the seam names its kind. */
  sourceKey: string;
  fileTypeId: string | null;
  fileTypeName: string | null;
  confidential: boolean;
  mappedBy: string | null;
  mappedAt: string | null;
  /** Each active type that applies to the record the document becomes and runs no photo pipeline. */
  fileTypes: { id: string; name: string }[];
}

/** 🔒 One type's answer (decision 3480), replacing whatever answer it had whole. */
export interface ImportAttachmentClassEdit {
  sourceKey: string;
  fileTypeId: string;
  confidential: boolean;
}

/** 🔒 The once-per-detail question's answer (the owner's rule of 2026-10-03): Yes creates the attribute, No ignores the detail. */
export interface ItemDetailAnswer {
  detailLabel: string;
  createAttribute: boolean;
}
export interface ItemDetailTargetEdit {
  detailLabel: string;
  target: ItemDetailTargetValue;
  attributeTypeId?: string | null;
  /** The Item column an ItemField target writes (decision 1921). */
  itemField?: ItemDetailItemField | null;
}
/** A value rule set, cleared with both ids empty, or moved one place by its id. */
export type ItemDetailValueEdit =
  | { detailLabel: string; sourceValue: string; categoryId?: string | null; taxCategoryId?: string | null }
  | { ruleId: string; move: 'up' | 'down' };

export type ImportAccountTypeEdit = Partial<Pick<ImportAccountTypeMappingView, 'accountType' | 'detailType' | 'cashFlowSection' | 'bandFrom' | 'bandTo' | 'isActive'>>;
export type ImportFieldEdit = Partial<Pick<ImportFieldMappingView, 'targetField' | 'conversion' | 'isActive'>>;
/** 🔨 A rule is graded, enabled and moved; its key kind is never an edit. */
export type ImportMatchRuleEdit = Partial<Pick<ImportMatchRuleView, 'grade' | 'isActive'>> & { move?: 'up' | 'down' };

export const IMPORT_MAPPINGS_PATH = '/import/mappings';
export const IMPORT_MAPPINGS_ROUTE = '/settings/system/import-mappings';
export const IMPORT_ITEM_DETAILS_PATH = `${IMPORT_MAPPINGS_PATH}/item-details`;
export const IMPORT_ITEM_DETAIL_ANSWER_PATH = `${IMPORT_MAPPINGS_PATH}/item-details/answer`;
export const IMPORT_ITEM_DETAIL_VALUES_PATH = `${IMPORT_MAPPINGS_PATH}/item-details/values`;
/** 🔒 Decision 3480: one Books document type's attachment answer, saved whole. */
export const IMPORT_ATTACHMENT_CLASSES_PATH = `${IMPORT_MAPPINGS_PATH}/attachments`;

export function importAccountTypeMappingPath(id: string): string {
  return `${IMPORT_MAPPINGS_PATH}/account-types/${encodeURIComponent(id)}`;
}
export function importFieldMappingPath(id: string): string {
  return `${IMPORT_MAPPINGS_PATH}/fields/${encodeURIComponent(id)}`;
}
export function importMatchRulePath(id: string): string {
  return `${IMPORT_MAPPINGS_PATH}/rules/${encodeURIComponent(id)}`;
}
